import React, { Component } from 'react'
import { Circle } from 'rc-progress'
import * as s from './style.scss'

const GAP_DEGREE = 80

// ---------- tiny colour helpers, so every ring colour is derived from the mentor's own hue ----------

// '#rgb' or '#rrggbb' -> [r, g, b]
const toRgb = (hex: string) => {
    let h = hex.replace('#', '')
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

const toHex = (rgb: number[]) =>
    '#' +
    rgb
        .map(v => {
            const clamped = Math.max(0, Math.min(255, Math.round(v)))
            return (clamped < 16 ? '0' : '') + clamped.toString(16)
        })
        .join('')

// blend two colours; amount 0 = all of `a`, 1 = all of `b`
const mix = (a: string, b: string, amount: number) => {
    const ra = toRgb(a)
    const rb = toRgb(b)
    return toHex(ra.map((v, i) => v + (rb[i] - v) * amount))
}

const lighten = (hex: string, amount: number) => mix(hex, '#ffffff', amount)
const darken = (hex: string, amount: number) => mix(hex, '#000000', amount)

// ring colours derived from the mentor's own hue, so every box gets its own set and nothing
// clashes. "occurred" stays the mentor colour, never overridden.
//
// "remaining" takes the deep end of the ramp rather than the pale end: a pale track keeps the
// ring hierarchy intact, while a dark dot and count read as a real value instead of a disabled
// one — and stay legible on all ten mentor colours (the pale variants drop under 3:1 on lime).
const ringColors = (c: string) => ({
    notOccurred: lighten(c, 0.45),
    notDocumented: lighten(c, 0.75),
    remaining: lighten(c, 0.88),
    remainingDot: darken(c, 0.35),
    remainingCount: darken(c, 0.45)
})

// cancelled meetings are the 'not_occurred' rows. Appended to the total line only when there are
// any — with none, the parenthetical is dropped rather than reading "(בוטלו 0 פגישות)"
const cancelledSuffix = (count: number) => {
    if (!count) return ''
    return count === 1 ? ' (בוטלה פגישה אחת)' : ` (בוטלו ${count} פגישות)`
}

interface MentorStats {
    id: number
    name: string
    total: number
    occurred: any[]
    notOccurred: any[]
    notDocumented: any[]
    rest: any[]
    occurredPercent: number
}

interface GoalStats {
    id: number
    name: string
    goal: number
    createdCount: number
}

interface MentorCardProps {
    mentor: MentorStats | GoalStats
    color: string
    viewMode?: 'daily' | 'monthly'
}

const isGoalStats = (mentor: MentorStats | GoalStats): mentor is GoalStats => (mentor as GoalStats).goal !== undefined

// mirrors rc-progress's own math (Circle.js getPathStyles) so hover matches what's drawn
const angleToSegmentIndex = (angleDeg: number, percents: number[]) => {
    // rc-progress draws clockwise from the gap, gap centered at bottom (90deg in screen terms)
    const drawStart = 90 + GAP_DEGREE / 2
    const sweep = ((angleDeg - drawStart) % 360 + 360) % 360
    const totalSweepDeg = 360 - GAP_DEGREE
    if (sweep > totalSweepDeg) return -1 // inside the gap itself
    let stackDeg = 0
    for (let i = 0; i < percents.length; i++) {
        const segDeg = (percents[i] / 100) * totalSweepDeg
        if (sweep <= stackDeg + segDeg) return i
        stackDeg += segDeg
    }
    return -1
}

interface MentorCardState {
    hover: { label: string; x: number; y: number } | null
}

class MentorCard extends Component<MentorCardProps, MentorCardState> {
    wrapRef = React.createRef<HTMLDivElement>()
    state: MentorCardState = { hover: null }

    handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, segments: { percent: number; label: string; count: number }[]) => {
        const rect = this.wrapRef.current.getBoundingClientRect()
        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const dx = e.clientX - cx
        const dy = e.clientY - cy
        const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI

        const idx = angleToSegmentIndex(angleDeg, segments.map(s => s.percent))
        if (idx === -1) {
            this.setState({ hover: null })
            return
        }
        const seg = segments[idx]
        this.setState({ hover: { label: `${seg.label}: ${seg.count}`, x: e.clientX - rect.left, y: e.clientY - rect.top } })
    }

    renderGoalCard(mentor: GoalStats, color: string) {
        const goalPct = mentor.goal ? Math.min(Math.round((mentor.createdCount / mentor.goal) * 100), 100) : 0
        const remainingCount = Math.max(mentor.goal - mentor.createdCount, 0)
        const ring = ringColors(color)

        return (
            <div className={`${s.card} ${s.mentorBox}`}>
                <div className={s.mentorBoxHeader}>
                    <span className={s.mentorBoxDot} style={{ background: color }} />
                    {mentor.name}
                </div>

                <div className={s.mentorBoxTotal}>
                    <bdi>{`${mentor.createdCount} / ${mentor.goal}`}</bdi>
                    {' פגישות'}
                </div>

                <div className={s.gauge}>
                    <div className={s.arcWrap}>
                        <Circle
                            percent={[goalPct]}
                            strokeColor={[color]}
                            strokeWidth={9}
                            trailWidth={9}
                            trailColor={ring.remaining}
                            gapDegree={GAP_DEGREE}
                            gapPosition="bottom"
                        />
                        <div className={s.arcCenter}>
                            <div className={s.gaugeValue}>{`${goalPct}%`}</div>
                            <div className={s.gaugeCaption}>{'מהיעד'}</div>
                        </div>
                    </div>
                </div>

                <div className={s.mentorSectionsRow}>
                    <div className={s.mentorSection}>
                        <div className={s.mentorSectionTitle}>
                            <span className={s.dot} style={{ background: color }} />
                            {'פגישות שנקבעו'}
                        </div>
                        <div className={s.mentorSectionCount} style={{ color }}>
                            {mentor.createdCount}
                        </div>
                    </div>

                    <div className={s.mentorSection}>
                        <div className={s.mentorSectionTitle}>
                            <span className={s.dot} style={{ background: ring.remainingDot }} />
                            {'נותרו ליעד'}
                        </div>
                        <div className={s.mentorSectionCount} style={{ color: ring.remainingCount }}>
                            {remainingCount}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const { mentor, color } = this.props

        if (isGoalStats(mentor)) return this.renderGoalCard(mentor, color)

        const { hover } = this.state

        // every slice is a share of the same total; the last slice takes the remainder instead of
        // its own rounded share, so the three always sum to exactly 100 (independent rounding can overflow, e.g. 101)
        const share = (n: number) => (mentor.total ? Math.round((n / mentor.total) * 100) : 0)
        const occurredPct = mentor.occurredPercent
        const notDocumentedPct = share(mentor.notDocumented.length)
        const restCount = mentor.rest.length + mentor.notOccurred.length
        const restPct = mentor.total ? 100 - occurredPct - notDocumentedPct : 0

        // the other ring colours are derived from this mentor's colour, so each box gets its own set
        const ring = ringColors(color)

        const segments = [
            { percent: occurredPct, color, label: 'פגישות שהתקיימו', count: mentor.occurred.length },
            {
                percent: notDocumentedPct,
                color: ring.notOccurred,
                label: 'פגישות שלא תועדו',
                count: mentor.notDocumented.length
            },
            {
                percent: restPct,
                color: ring.notDocumented,
                label: 'פגישות שנותרו',
                count: restCount
            }
        ]

        return (
            <div className={`${s.card} ${s.mentorBox}`}>
                <div className={s.mentorBoxHeader}>
                    <span className={s.mentorBoxDot} style={{ background: color }} />
                    {mentor.name}
                </div>

                <div className={s.mentorBoxTotal}>
                    {mentor.total === 0
                        ? this.props.viewMode === 'daily'
                            ? 'אין פגישות היום'
                            : 'אין פגישות החודש'
                        : `סך הכל ${mentor.total} פגישות ${
                              this.props.viewMode === 'daily' ? 'היום' : 'החודש'
                          }${cancelledSuffix(mentor.notOccurred.length)}`}
                </div>

                <div className={s.gauge}>
                    <div
                        ref={this.wrapRef}
                        className={s.arcWrap}
                        onMouseMove={e => mentor.total !== 0 && this.handleMouseMove(e, segments)}
                        onMouseLeave={() => this.setState({ hover: null })}
                    >
                        <Circle
                            percent={mentor.total === 0 ? [0] : segments.map(seg => seg.percent)}
                            strokeColor={mentor.total === 0 ? ['#d9d9d9'] : segments.map(seg => seg.color)}
                            strokeWidth={9}
                            trailWidth={9}
                            trailColor={ring.remaining}
                            gapDegree={GAP_DEGREE}
                            gapPosition="bottom"
                        />
                        <div className={s.arcCenter}>
                            <div className={s.gaugeValue}>{mentor.total === 0 ? '--' : `${occurredPct}%`}</div>
                            <div className={s.gaugeCaption}>{'פגישות שבוצעו'}</div>
                        </div>
                        {hover && (
                            <div className={s.arcTooltip} style={{ left: hover.x, top: hover.y }}>
                                {hover.label}
                            </div>
                        )}
                    </div>
                </div>

                <div className={s.mentorSectionsRow}>
                    <div className={s.mentorSection}>
                        <div className={s.mentorSectionTitle}>
                            <span className={s.dot} style={{ background: color }} />
                            {'פגישות שהתקיימו'}
                        </div>
                        <div className={s.mentorSectionCount} style={{ color }}>
                            {mentor.occurred.length}
                        </div>
                    </div>

                    <div className={s.mentorSection}>
                        <div className={s.mentorSectionTitle}>
                            <span className={s.dot} style={{ background: ring.remainingDot }} />
                            {'פגישות שנותרו'}
                        </div>
                        <div className={s.mentorSectionCount} style={{ color: ring.remainingCount }}>
                            {mentor.rest.length}
                        </div>
                    </div>

                </div>
            </div>
        )
    }
}

export default MentorCard
